import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { HeaderAnnouncement } from 'admin-common';
import { filter, map, Observable, shareReplay } from 'rxjs';

/**
 * Marketing bar under the header (same as setycore, which reads it from its runtime config). WUnivers has no bank
 * financing, so it presents what the shop sells. Its texts are translated under `layout.announcements.<id>`;
 * set it to null to hide the bar.
 */
const HEADER_ANNOUNCEMENT: HeaderAnnouncement | null = {
  id: 'tout-pour-votre-bureau',
  emoji: '🖥️',
  title: 'Tout pour votre bureau',
  message:
    'ordinateurs, imprimantes, consommables et logiciels, livrés dans les 69 wilayas ou disponibles dans nos magasins.',
  linkLabel: 'Découvrir nos produits',
  linkUrl: '/products',
};

/** The `layout` translation namespace (header, footer, config-driven texts). */
export interface LayoutTexts {
  header: Record<string, any>;
  footer: Record<string, any>;
  /** Translations of the announcement, by announcement id. */
  announcements?: Record<string, Partial<HeaderAnnouncement>>;
}

/**
 * Texts of the header and footer in the current language. Header and footer stay alive across language
 * changes (only the routed page is re-created), so they listen to these streams instead of reading once.
 */
@Injectable({ providedIn: 'root' })
export class LayoutTextsService {
  /** Re-emits after each language change, once its translations are loaded. */
  readonly layout$: Observable<LayoutTexts>;

  /** Marketing bar of the header (translated when a translation exists); null hides it. */
  readonly announcement$: Observable<HeaderAnnouncement | null>;

  constructor(translate: TranslateService) {
    this.layout$ = translate.stream('layout').pipe(
      // before the translations are loaded, ngx-translate returns the key itself
      filter((layout): layout is LayoutTexts => !!layout && typeof layout === 'object'),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.announcement$ = this.layout$.pipe(
      map((layout) =>
        HEADER_ANNOUNCEMENT
          ? { ...HEADER_ANNOUNCEMENT, ...(layout.announcements?.[HEADER_ANNOUNCEMENT.id ?? ''] ?? {}) }
          : null,
      ),
    );
  }
}
